<script setup lang="ts">
/**
 * VoicePadlock — the ONE place voice is sold (job #490).
 *
 * Tom's ruling, 2026-09-21: voice — voice notes in groups, huddles, the
 * two-minute calls — is a single per-learner entitlement at £5 a month, the
 * £15 plan includes it, and it shows ONLY at the padlock: the first time a
 * free learner taps anything voice they meet this, and nowhere else. No
 * pricing page, no menu entry. Padlock = entitlement only; money fixes it.
 *
 * HOW A VOICE ACTION MOUNTS IT:
 *
 *   const voice = useVoiceEntitlement()
 *   const padlock = ref(false)
 *   async function onTapRecord() {
 *     if (!(await voice.ensure())) { padlock.value = true; return }
 *     …start the thing…
 *   }
 *   <VoicePadlock v-if="padlock" @close="padlock = false" @unlocked="onTapRecord" />
 *
 * WHAT IT DOES: shows the lock, one line saying voice is £5 a month, and a
 * button that opens the EXISTING checkout (useCheckout, plan 'voice') in the
 * app's own safe-area overlay. Paddle's completion event puts the app in the
 * pending-purchase wait, which polls the voice flag; when it lands this emits
 * `unlocked` so the caller can carry on with what they tapped.
 *
 * The price is the live SSi Voice price (lib/paddle.ts). Should the server
 * ever answer with no price id, the button says voice is not on sale yet
 * rather than opening a Paddle error.
 *
 * Learner-facing, so every string goes through t(); the product name is a
 * brand mark and is not translated.
 */
import { computed, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import { useCheckout, storeNoticeKey } from '../composables/useCheckout'
import { paymentRoute, storeBillingLive } from '../platform/paymentRoute'
import { useVoiceEntitlement, type PadlockPlan } from '../composables/useVoiceEntitlement'
import { useEntitlementGrid, type GridThing } from '../composables/useEntitlementGrid'

// SINCE JOB #576 THIS IS THE ONE PADLOCK. `plan` says what it sells: 'voice'
// is the £5 feature, as before; 'premium' is the £15 plan — the wall a
// premium course's Listening Mode, main flow past yellow belt, or voice in a
// premium language all stand behind. `thing` names the cell so the padlock
// can say the right line and go the moment that cell opens.

const props = withDefaults(
  defineProps<{
    /** Where Paddle's success redirect lands. Defaults to the current screen. */
    returnTo?: string | null
    /** The £5 voice feature, or the £15 plan. */
    plan?: PadlockPlan
    /** Which cell of the grid this padlock stands in front of. */
    thing?: GridThing | null
    /** The course, for a 'premium' padlock: the checkout is opened for it and its cell watched. */
    courseCode?: string | null
  }>(),
  { returnTo: null, plan: 'voice', thing: null, courseCode: null },
)

const emit = defineEmits<{
  /** They backed out. Unmount. */
  (e: 'close'): void
  /** The entitlement arrived while this was mounted. Carry on with the action. */
  (e: 'unlocked'): void
}>()

const { t } = useI18n()
const voice = useVoiceEntitlement()
const grid = useEntitlementGrid()
const checkout = useCheckout()

const isPlan = computed(() => props.plan === 'premium')
// JOB #689: a school learner meets the £5 personal premium at the £15 padlock, on
// any course including a free-language class. It is the same access for less, so
// it replaces the £15 button rather than sitting beside it. Schools pay for class
// use, never for a learner's own account, so this shows even when the school covers the course.
const schoolClassId = computed(() => (isPlan.value ? voice.schoolClassId.value : null))
// THE APP (plan 032). Inside the iOS and Android apps the store sells Premium
// and nothing else: a Premium padlock with no class is on sale at the store's
// own monthly price; a voice or school padlock is not for sale and names no
// price, because the web's price is not the app's to show. While store billing
// is off for this shell (the default) nothing is on sale here at all: the card
// says so and its button stays disabled, never a button that does nothing.
const inApp = paymentRoute() === 'store'
const storeLive = inApp && storeBillingLive()
const storeMonthly = computed(() => checkout.storeProducts?.value.find((p) => p.period === 'monthly')?.priceLabel ?? '')
if (storeLive && isPlan.value) void checkout.refreshStoreProducts?.()
// In the app the store sells Premium (plan 032), and the padlock is ON SALE only
// with the store's monthly price beside the button (plan 040): with no price, from
// a store that has not answered or a monthly plan hidden for an offer, the button
// would buy a plan nobody was shown. The £15 web plan is always on sale: its price
// ids ship in the repo (lib/paddle.ts).
const storeSellsPlan = computed(() => storeLive && isPlan.value && !schoolLearner.value)
const forSale = computed(() => (inApp ? storeSellsPlan.value && !!storeMonthly.value : isPlan.value ? true : !!voice.price.value.priceId))
const title = computed(() => (isPlan.value ? t('padlock.planTitle') : t('voice.title')))
const schoolLearner = computed(() => !!schoolClassId.value)
// t() takes no parameters (useI18n.ts:215) — the price is spliced in here, as PlanPicker does.
const line = computed(() => {
  if (inApp) {
    if (storeSellsPlan.value) return t('padlock.planStoreLine')
    return storeLive || !isPlan.value || schoolLearner.value ? t('padlock.notSoldInApp') : t('settings.subscriptionsNotAvailableHere')
  }
  if (!isPlan.value) return t('voice.line').replace('{price}', voice.priceLine.value)
  if (schoolLearner.value) return t('padlock.schoolLearnerLine')
  const thing = props.thing
  if (thing === 'listening_mode') return t('padlock.planListening')
  if (thing === 'group_voice_note' || thing === 'huddle' || thing === 'connect_call') return t('padlock.planVoice')
  return t('padlock.planMainFlow')
})
const unlockLabel = computed(() => {
  if (inApp) {
    if (!forSale.value) return isPlan.value ? t('padlock.planTitle') : t('voice.notForSale')
    return storeMonthly.value ? t('padlock.planUnlockStore').replace('{price}', storeMonthly.value) : t('padlock.planTitle')
  }
  if (schoolLearner.value) return t('padlock.schoolLearnerUnlock')
  if (isPlan.value) return t('padlock.planUnlock')
  return forSale.value ? t('voice.unlock').replace('{price}', voice.priceLine.value) : t('voice.notForSale')
})
const busy = computed(() => checkout.isOpeningCheckout.value || voice.loading.value || grid.loading.value)
// What the last store purchase said, if it did not simply succeed (plan 032).
const storeNoticeText = computed(() => {
  const notice = inApp ? checkout.storeNotice?.value : ''
  if (!notice) return ''
  const text = t(storeNoticeKey(notice))
  const reference = checkout.storeReference?.value
  return reference && notice !== 'pending' ? `${text} ${t('purchasePending.reference')}: ${reference}` : text
})

async function unlock(): Promise<void> {
  if (!forSale.value) return
  const returnTo = props.returnTo ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')
  if (isPlan.value) {
    await checkout.startCheckout({
      plan: 'premium', billingPeriod: 'monthly', returnTo, courseCode: props.courseCode ?? null,
      ...(schoolClassId.value ? { schoolClassId: schoolClassId.value } : {}),
    })
    return
  }
  await checkout.startCheckout({ plan: 'voice', billingPeriod: 'monthly', returnTo })
}

// The padlock is the entitlement's negative space: the moment the flag turns
// true — purchase landed, or a plan somebody else pays for arrived — it goes.
// A 'premium' padlock watches its own cell of the course grid instead.
watch(
  () => voice.entitled.value,
  (on) => {
    if (on && !isPlan.value) emit('unlocked')
  },
)
watch(
  () => (isPlan.value && props.courseCode && props.thing ? grid.cellSync(props.courseCode, props.thing)?.allowed : false),
  (on) => {
    if (on) emit('unlocked')
  },
)
</script>

<template>
  <!-- Teleported to <body>: every screen that mounts this sits inside some
       stacking context of its own — the player's root is position: fixed —
       so a z-index here only ranks INSIDE that parent. On the player the
       resting state's course flag and title (z-index 50, a sibling of the
       player root) painted over the card and hid "Not now" (Aran,
       Chromebook, 2026-09-24). At body level 8500 outranks all of it. -->
  <Teleport to="body">
  <div class="voice-padlock" role="dialog" aria-modal="true" :aria-label="title" :data-plan="plan">
    <div class="voice-padlock-card">
      <svg
        class="voice-padlock-glyph"
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true" focusable="false"
      >
        <rect x="4" y="10.5" width="16" height="10.5" rx="2"/>
        <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/>
      </svg>
      <h2 class="voice-padlock-title">{{ title }}</h2>
      <p class="voice-padlock-line">{{ line }}</p>
      <p v-if="checkout.checkoutError.value" class="voice-padlock-error" role="alert">{{ checkout.checkoutError.value }}</p>
      <p v-if="storeNoticeText" class="voice-padlock-error" role="alert">{{ storeNoticeText }}</p>
      <button
        type="button"
        class="voice-padlock-unlock"
        :disabled="!forSale || busy"
        :aria-disabled="!forSale || busy"
        @click="unlock"
      >
        {{ unlockLabel }}
      </button>
      <button type="button" class="voice-padlock-close" @click="emit('close')">{{ t('voice.notNow') }}</button>
    </div>
  </div>
  </Teleport>
</template>

<style scoped>
.voice-padlock {
  position: fixed;
  inset: 0;
  z-index: 8500; /* under the sign-in sheet (8800) and checkout (9000), over everything else */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  padding-top: max(1rem, env(safe-area-inset-top));
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
  padding-left: max(1rem, env(safe-area-inset-left));
  padding-right: max(1rem, env(safe-area-inset-right));
  box-sizing: border-box;
}
.voice-padlock-card {
  width: 100%;
  max-width: 22rem;
  background: var(--bg-elevated, #ffffff);
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 1.5rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.voice-padlock-glyph {
  width: 2.75rem;
  height: 2.75rem;
  color: var(--accent, #c23a3a);
}
.voice-padlock-title {
  margin: 0.75rem 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary, #1e293b);
}
.voice-padlock-line {
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: var(--text-secondary, #64748b);
}
.voice-padlock-error {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: var(--accent, #c23a3a);
}
.voice-padlock-unlock {
  width: 100%;
  min-height: 2.75rem;
  padding: 0.7rem 1rem;
  border: none;
  border-radius: 0.75rem;
  background: var(--accent, #c23a3a);
  color: #fff;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
}
.voice-padlock-unlock:hover { filter: brightness(1.08); }
.voice-padlock-unlock:disabled { opacity: 0.65; cursor: default; filter: none; }
.voice-padlock-close {
  margin-top: 0.5rem;
  min-height: 2.75rem; /* 44px — a comfortable tap target */
  min-width: 2.75rem;
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  color: var(--text-secondary, #64748b);
  font-family: var(--font-body);
  font-size: 0.9375rem;
  cursor: pointer;
}
</style>
